import { Benefit } from "#/lib/carrot-api/types/Benefit"
import { useProviderTypeSearched } from "#/pages/provider-finder/components/results/useProviderTypeSearched"
import { Provider } from "#/pages/provider-finder/types/Providers"
import { useTextDirection } from "#/services/locale/hooks/useTextDirection"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { getPiiProps } from "#/services/tracking"
import {
  BadgeIcon,
  Box,
  Link,
  LocationIcon,
  Snackbar,
  Tooltip,
  TrophyIcon,
  Typography,
  Button
} from "@carrotfertility/carotene-core"
import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { Link as ReactRouterLink } from "react-router-dom"
import { TextDirection } from "#/types/Locale"
import { ProviderChips } from "#/pages/provider-finder/components/results/ProviderChips"
import { Paths } from "#/utils/Paths"
import { useCustomNetworkCustomer } from "#/pages/provider-finder/utils/useCustomNetworkCustomer"
import { VirtualServiceRestrictionNotice } from "#/pages/provider-finder/components/results/VirtualServiceRestrictionNotice"

export function DirectionsLink({
  address,
  searchLocation,
  providerUuid
}: {
  address: string
  searchLocation: string
  providerUuid: number
}): JSX.Element {
  const { mutate: trackMemberAction } = useMemberActionTracking()
  const { isDoulasSearched } = useProviderTypeSearched()

  const getGoogleDirections = (address: string): string => {
    // The structure of the url is `/maps/dir/<starting point>/<destination>`
    const googleBaseUrl = "https://www.google.com/maps/dir"
    const encodedSearchLocation = encodeURIComponent(searchLocation)
    const encodedAddress = encodeURIComponent(address)
    return `${googleBaseUrl}/${encodedSearchLocation}/${encodedAddress}`
  }
  return address && !isDoulasSearched ? (
    <Link
      data-id="provider-directions-link"
      href={getGoogleDirections(address)}
      target="_blank"
      color="inherit"
      onClick={() => {
        trackMemberAction({
          memberActionTaken: MemberActions.PROVIDER_CARD_INTERACTION_DIRECTIONS,
          metaData: {
            providerUuid
          }
        })
      }}
    >
      <FormattedMessage
        defaultMessage="Directions"
        description="Clicking on the directions link will open up Google Maps to get the directions to this provider's location"
      />
    </Link>
  ) : null
}

function IconChips({
  carrotCenterOfExcellence,
  carrotPartner
}: {
  carrotCenterOfExcellence: boolean
  carrotPartner: boolean
}): JSX.Element | null {
  // Return null if both chips would be hidden
  if (!carrotCenterOfExcellence && !carrotPartner) return null

  return (
    <Box sx={{ display: "flex", flexDirection: "row" }} gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
      {carrotCenterOfExcellence && (
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
        >
          <TrophyIcon fontSize="small" color="primary" />
          <Typography fontWeight="bold" textTransform="uppercase" fontSize="0.75rem">
            <FormattedMessage defaultMessage="Top success rates" />
          </Typography>
        </Box>
      )}
      {carrotPartner && (
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
        >
          <BadgeIcon fontSize="small" color="primary" />
          <Typography fontWeight="bold" textTransform="uppercase" fontSize="0.75rem">
            <FormattedMessage defaultMessage="Exclusive benefits" />
          </Typography>
        </Box>
      )}
    </Box>
  )
}

function LocationMayVary({ locationMayVary }: { locationMayVary: boolean }): JSX.Element {
  return locationMayVary ? (
    <Box
      sx={{ display: "flex", flexDirection: "row", alignItems: "start" }}
      gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
    >
      <LocationIcon color="secondary" />
      <Typography variant="body2" color="secondary" sx={{ wordBreak: "break-word" }} fontStyle="italic">
        <FormattedMessage defaultMessage="Contact the provider to confirm. They may offer services virtually or from multiple locations." />
      </Typography>
    </Box>
  ) : null
}

function RequestAppointmentButton({
  providerName,
  providerUuid,
  carrotPartner
}: {
  providerName: string
  providerUuid: number
  carrotPartner: boolean
}): JSX.Element {
  const { mutate } = useMemberActionTracking()
  const intl = useIntl()

  return carrotPartner ? (
    <Button
      variant="outlined"
      size="small"
      color="secondary"
      component={ReactRouterLink}
      to={`${Paths.PROVIDER_FINDER}/requestappointment/${encodeURIComponent(providerName)}/${providerUuid}`}
      title={intl.formatMessage({ defaultMessage: "Contact our team to book an appointment" })}
      onClick={() => {
        mutate({
          memberActionTaken: MemberActions.PROVIDER_CARD_INTERACTION_REQUEST_APPOINTMENT,
          metaData: {
            providerUuid
          }
        })
      }}
      sx={{ inlineSize: "fit-content" }}
      data-id="provider-request-appt-button"
      data-provider-name={{ providerName }}
    >
      <FormattedMessage
        defaultMessage="Request appointment"
        description="Clicking on the Request appointment button will redirect you to directions on how to book an appointment at a provider"
      />
    </Button>
  ) : null
}

function ProviderEmail({ email }: { email: string }): JSX.Element {
  const intl = useIntl()
  const [copiedEmail, setCopiedEmail] = React.useState<boolean>(false)
  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText(email)
    setCopiedEmail(true)
    setTimeout(() => setCopiedEmail(false), 2400)
  }

  const textDirection = useTextDirection()

  return email ? (
    <>
      <Tooltip
        disableHoverListener={false}
        title={intl.formatMessage({ defaultMessage: "Copy email" })}
        placement="bottom"
      >
        {/* Box is needed to enable hover even on Link */}
        <Box>
          <Link color="inherit" component="button" onClick={handleCopyEmail}>
            {intl.formatMessage({ defaultMessage: "Email" })}
          </Link>
        </Box>
      </Tooltip>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: textDirection === TextDirection.RightToLeft ? "right" : "left"
        }}
        open={copiedEmail}
        message={intl.formatMessage({ defaultMessage: "The email was copied. " })}
      />
    </>
  ) : null
}

function ProviderWebsite({ website, providerUuid }: { website: string; providerUuid: number }): JSX.Element {
  const { mutate } = useMemberActionTracking()
  return website ? (
    <Link
      data-id="provider-website-link"
      href={website}
      target="_blank"
      rel="noreferrer noopener"
      color="inherit"
      onClick={() => {
        mutate({
          memberActionTaken: MemberActions.PROVIDER_CARD_INTERACTION_WEBSITE,
          metaData: {
            providerUuid
          }
        })
      }}
    >
      <FormattedMessage defaultMessage="Website" />
    </Link>
  ) : null
}

export function ProviderPhone({ phone, providerUuid }: { phone: string; providerUuid: number }): JSX.Element {
  const { mutate } = useMemberActionTracking()
  return phone ? (
    <Link
      data-id="provider-phone-link"
      dir="ltr"
      href={`tel:${phone}`}
      color="inherit"
      onClick={() => {
        mutate({
          memberActionTaken: MemberActions.PROVIDER_CARD_INTERACTION_PHONE_NUMBER,
          metaData: {
            providerUuid
          }
        })
      }}
    >
      {phone}
    </Link>
  ) : null
}

type ProviderDetailsMainContentProps = {
  provider: Provider
  searchLocation: string
  benefit: Benefit
}

export function ProviderDetailsMainContent({
  provider,
  benefit,
  searchLocation
}: ProviderDetailsMainContentProps): JSX.Element {
  const {
    providerName,
    address,
    phone,
    email,
    website,
    carrotPartner,
    locationMayVary,
    providerUuid,
    carrotCenterOfExcellence,
    virtualServicesNotAvailableIn
  } = provider
  const { isUPennSegment } = useCustomNetworkCustomer()

  return (
    <Box
      {...getPiiProps()}
      gap={(theme) => theme.spacing(theme.tokens.spacing.md)}
      sx={{ maxInlineSize: "sm", display: "flex", flexDirection: "column" }}
    >
      <IconChips carrotCenterOfExcellence={!isUPennSegment && carrotCenterOfExcellence} carrotPartner={carrotPartner} />
      <Typography variant="h4" component="h3" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        {providerName}
      </Typography>
      <Typography sx={(theme) => ({ color: theme.palette.text.secondary, wordBreak: "break-word" })}>
        {address}
      </Typography>
      <LocationMayVary {...{ locationMayVary }} />
      <VirtualServiceRestrictionNotice {...{ virtualServicesNotAvailableIn }} />
      <Box
        gap={(theme) => `${theme.spacing(theme.tokens.spacing.xs)} ${theme.spacing(theme.tokens.spacing.lg)}`}
        sx={{ whiteSpace: "nowrap", flexWrap: "wrap", display: "flex", flexDirection: "row" }}
      >
        <ProviderPhone {...{ phone, providerUuid }} />
        <ProviderEmail {...{ email }} />
        <ProviderWebsite {...{ website, providerUuid }} />
        <DirectionsLink {...{ address, searchLocation, providerUuid }} />
      </Box>
      <ProviderChips {...{ provider, benefit }} />
      <RequestAppointmentButton {...{ providerName, providerUuid, carrotPartner }} />
    </Box>
  )
}
